import { operationalSelectedScopeId } from '../../runtime/operational-selected-scope';
import type { HazardImmediateAction, HazardReportSubmission, HazardType } from './model';

/**
 * Client half of the `hazard-report-operations-production-wave` contract.
 *
 * 현장 위험요인 제안 does not ride on the 시스템 개선 feedback wave. The deployed feedback
 * callables whitelist their envelope key by key (`report` may carry only category, description,
 * route, occurredAt), gate on `feedback:submit` / `feedback:triage`, write into
 * `tenants/{tenantId}/feedback` with no way to filter one kind out of a list page, and run a
 * triage lifecycle built around reproducing a software defect. A plant hazard needs its own
 * fields, its own readers, its own capability and a route onward to 개선 요청 or 안전수칙 위반.
 * So it gets its own collection and its own callables, and every document carries
 * `kind: 'field-hazard'` so the two are separable in the data even if a report ever has to be
 * read alongside feedback.
 *
 * Nothing in this module writes a report anywhere but the server. There is no local fallback
 * repository on purpose: an unsent hazard report must never look accepted.
 */

export type HazardReportScope =
  | { kind: 'tenant'; tenantId: string }
  | { kind: 'site'; tenantId: string; siteId: string }
  | { kind: 'contractor'; tenantId: string; siteId: string; contractorId: string };

export type HazardReportStatus =
  'received' | 'acknowledged' | 'improvement-linked' | 'violation-linked' | 'resolved' | 'rejected';

export type HazardReportRecord = {
  hazardReportId: string;
  kind: 'field-hazard';
  version: number;
  scope: HazardReportScope;
  reporter: { identityId: string; displayName: string };
  report: {
    hazardType: HazardType;
    location: string;
    description: string;
    immediateAction: HazardImmediateAction;
    occurredAt: string;
  };
  status: HazardReportStatus;
  link: { improvementId: string | null; violationId: string | null };
  allowedActions: readonly string[];
  createdAt: string;
  updatedAt: string;
};

export type SubmitHazardReportInput = {
  scope: HazardReportScope;
  hazardReportId: string;
  commandId: string;
  idempotencyKey: string;
  report: HazardReportSubmission['report'];
};

export interface HazardReportRepository {
  submit(input: SubmitHazardReportInput): Promise<HazardReportRecord>;
  listOwn(scope: HazardReportScope): Promise<HazardReportRecord[]>;
}

/**
 * The six callables the server wave must expose, named exactly as the client calls them.
 * Kept `unknown` at the edges, like the feedback port, so every field is validated below rather
 * than trusted from a response shape.
 */
export type HazardReportOperationsCallablePort = {
  submit(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  listOwn(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  detail(input: Readonly<Record<string, unknown>>): Promise<unknown>;
};

const hazardTypeValues = new Set<string>([
  'fall',
  'caught',
  'struck',
  'electric',
  'fire-explosion',
  'chemical',
  'slip',
  'equipment',
  'other',
]);

const immediateActionValues = new Set<string>(['none', 'reported', 'isolated', 'stopped']);

const hazardStatusValues = new Set<string>([
  'received',
  'acknowledged',
  'improvement-linked',
  'violation-linked',
  'resolved',
  'rejected',
]);

/**
 * Shares the feedback grammar (`tenant:kop`, `site:<id>`, `site:<id>/contractor:<id>`).
 * Encodes the selected scope without guessing an alias or granting authority.
 */
export function hazardReportScopeId(scope: HazardReportScope): string | null {
  return operationalSelectedScopeId(scope);
}

/** Validates a server projection into a record. A missing field throws rather than defaulting. */
export function toHazardReportRecord(scope: HazardReportScope, value: unknown): HazardReportRecord {
  const malformed = () => new Error('서버 응답 형식을 확인할 수 없습니다.');
  if (!value || typeof value !== 'object') throw malformed();
  const source = value as Record<string, unknown>;
  const { hazardReportId, kind, version, status, report, createdAt, updatedAt } = source;
  if (
    typeof hazardReportId !== 'string' ||
    !hazardReportId ||
    kind !== 'field-hazard' ||
    typeof version !== 'number' ||
    typeof status !== 'string' ||
    !hazardStatusValues.has(status) ||
    !report ||
    typeof report !== 'object' ||
    typeof createdAt !== 'string' ||
    typeof updatedAt !== 'string'
  )
    throw malformed();
  const reportValue = report as Record<string, unknown>;
  if (
    typeof reportValue.hazardType !== 'string' ||
    !hazardTypeValues.has(reportValue.hazardType) ||
    typeof reportValue.location !== 'string' ||
    typeof reportValue.description !== 'string' ||
    typeof reportValue.immediateAction !== 'string' ||
    !immediateActionValues.has(reportValue.immediateAction) ||
    typeof reportValue.occurredAt !== 'string'
  )
    throw malformed();
  const reporterValue = source.reporter as Record<string, unknown> | undefined;
  const linkValue = source.link as Record<string, unknown> | undefined;
  return {
    hazardReportId,
    kind: 'field-hazard',
    version,
    scope,
    reporter: {
      identityId: typeof reporterValue?.identityId === 'string' ? reporterValue.identityId : '',
      displayName: typeof reporterValue?.displayName === 'string' ? reporterValue.displayName : '',
    },
    report: {
      hazardType: reportValue.hazardType as HazardType,
      location: reportValue.location,
      description: reportValue.description,
      immediateAction: reportValue.immediateAction as HazardImmediateAction,
      occurredAt: reportValue.occurredAt,
    },
    status: status as HazardReportStatus,
    link: {
      improvementId: typeof linkValue?.improvementId === 'string' ? linkValue.improvementId : null,
      violationId: typeof linkValue?.violationId === 'string' ? linkValue.violationId : null,
    },
    // Absent rather than permissive when the server omits it, so any later action gate on this
    // record fails closed.
    allowedActions: Array.isArray(source.allowedActions)
      ? source.allowedActions.filter((action): action is string => typeof action === 'string')
      : [],
    createdAt,
    updatedAt,
  };
}

/**
 * Every read is a genuine round trip. `submit` reports success only after `loadHazardReport`
 * returns the persisted record, so nothing is ever shown as 접수됨 without a server receipt.
 */
export class ProductionHazardReportRepository implements HazardReportRepository {
  constructor(private readonly port: HazardReportOperationsCallablePort) {}

  private async loadDetailRecord(
    scope: HazardReportScope,
    hazardReportId: string,
  ): Promise<HazardReportRecord | null> {
    const selectedScopeId = hazardReportScopeId(scope);
    if (!selectedScopeId) throw new Error('허용되지 않은 범위입니다.');
    const response = (await this.port.detail({ selectedScopeId, hazardReportId })) as
      { report?: unknown } | null | undefined;
    const report = response?.report;
    if (report === null || report === undefined) return null;
    return toHazardReportRecord(scope, report);
  }

  async submit(input: SubmitHazardReportInput): Promise<HazardReportRecord> {
    const selectedScopeId = hazardReportScopeId(input.scope);
    if (!selectedScopeId) throw new Error('허용되지 않은 범위입니다.');
    await this.port.submit({
      selectedScopeId,
      hazardReportId: input.hazardReportId,
      commandId: input.commandId,
      idempotencyKey: input.idempotencyKey,
      report: input.report,
    });
    const persisted = await this.loadDetailRecord(input.scope, input.hazardReportId);
    if (!persisted)
      throw new Error('접수 결과를 서버에서 확인하지 못했습니다. 잠시 후 다시 확인해 주세요.');
    return persisted;
  }

  async listOwn(scope: HazardReportScope): Promise<HazardReportRecord[]> {
    const selectedScopeId = hazardReportScopeId(scope);
    if (!selectedScopeId) return [];
    const page = await this.port.listOwn({ selectedScopeId, pageSize: 25 });
    const items = (page as { items?: unknown } | null | undefined)?.items;
    if (!Array.isArray(items)) throw new Error('목록 응답 형식을 확인할 수 없습니다.');
    return items.map((item) => toHazardReportRecord(scope, item));
  }
}

export type HazardReportIntake =
  | { status: 'ready'; repository: HazardReportRepository }
  | { status: 'unavailable'; reason: string };

const unavailableReason =
  '현장 위험요인 접수 창구가 아직 열리지 않았습니다. 접수는 서버 확인 후에만 완료할 수 있으므로, 지금은 작성한 내용을 관리자에게 직접 전달해 주세요.';

/**
 * Turns ready the moment two things exist together: the deployed
 * `hazard-report-operations-production-wave` callables, and the matching
 * `createHazardReportOperationsPort` transport in `@kop/operations-firebase-adapter`. Until then
 * it reports unavailable, and the form holds its primary action closed with that reason. There is
 * deliberately no device-storage stand-in.
 */
export async function createHazardReportIntake(): Promise<HazardReportIntake> {
  if (import.meta.env.VITE_HAZARD_REPORT_DATA_ADAPTER !== 'firebase')
    return { status: 'unavailable', reason: unavailableReason };
  const config = {
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
  };
  if (Object.values(config).some((value) => value.trim().length === 0))
    return { status: 'unavailable', reason: unavailableReason };
  const adapter: Record<string, unknown> = await import('@kop/operations-firebase-adapter');
  const createPort = adapter.createHazardReportOperationsPort;
  if (typeof createPort !== 'function') return { status: 'unavailable', reason: unavailableReason };
  return {
    status: 'ready',
    repository: new ProductionHazardReportRepository(
      (createPort as (value: typeof config) => HazardReportOperationsCallablePort)(config),
    ),
  };
}
